import { useMemo } from "react";
import {
  AMAP_CHARGING_DESTINATION,
  type ChargingStation,
  type ChargingStationSource
} from "../../config/amapChargingDemo";
import type { CockpitCarwashData, CockpitFuelData, CockpitParcelData } from "../../context/CockpitDataContext";
import { getNightAiCardContent } from "../../config/nightAiCardContent";
import { parkingPaymentInfo } from "../../config/parkingPayment";
import { MAX_FUEL_RANGE_KM } from "../../config/vehicleEnergy";
import type { CabinState, ScenarioDecision } from "../../types/domain";
import { assetUrl } from "../../utils/assetUrl";
import { NightAiScenarioIcon } from "./NightAiScenarioIcon";
import { NightLargeGlowCardSurface } from "./NightLargeGlowCardSurface";
import "./NightCards.css";

const PARKING_PAYMENT_IMAGE_SRC = assetUrl("assets/figma/night-ai/alipay-parking-payment.svg");
interface NightAiExpandedCardProps {
  decision: ScenarioDecision;
  state: CabinState;
  chargingStation?: ChargingStation;
  chargingStationCount?: number;
  chargingStationSource?: ChargingStationSource;
  destinationName?: string;
  carwash?: CockpitCarwashData;
  fuel?: CockpitFuelData;
  parcel?: CockpitParcelData;
  parkingPaymentOpen?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onCancelParkingPayment?: () => void;
  onOpenParcelDetail?: () => void;
  onParkingPaymentPaid?: () => void;
}

export function NightAiExpandedCard({
  decision,
  state,
  chargingStation,
  chargingStationCount = 0,
  chargingStationSource = "loading",
  destinationName = AMAP_CHARGING_DESTINATION.name,
  carwash,
  fuel,
  parcel,
  parkingPaymentOpen = false,
  onClose,
  onConfirm,
  onCancelParkingPayment,
  onOpenParcelDetail,
  onParkingPaymentPaid
}: NightAiExpandedCardProps) {
  const content = getNightAiCardContent(decision.scenario.id);
  const refuelRecommendation = useMemo(
    () => buildRefuelRecommendation(state, fuel),
    [fuel, state.destinationDistanceKm, state.fuelLevel, state.fuelRangeKm, state.traffic]
  );

  const isRefuel = decision.scenario.id === "refuel";
  const isParking = decision.scenario.id === "parking";
  const isCarwash = decision.scenario.id === "carwash";
  const isParcel = decision.scenario.id === "parcel";
  const hasCarwashFacts = isCarwash && Boolean(carwash);
  const hasParcelFacts = isParcel && Boolean(parcel);
  const isCharging = decision.scenario.id === "charging" && chargingStation;
  const isLiteParcel = isParcel && state.liteMode;
  const isParkingPaymentCard = isParking && parkingPaymentOpen;
  const isLargeCard = isParkingPaymentCard;
  const expandedTitle = isParkingPaymentCard
      ? "支付宝停车缴费"
      : content.expandedTitle;
  const expandedDescription = isParkingPaymentCard
    ? `${parkingPaymentInfo.lotName} · ${parkingPaymentInfo.plateNumber} · 已停${parkingPaymentInfo.duration}`
    : isRefuel
    ? `${refuelRecommendation.location} ${refuelRecommendation.station}，建议加 ${refuelRecommendation.amount} ${refuelRecommendation.grade}，${refuelRecommendation.detour}。`
    : hasCarwashFacts
      ? buildCarwashExpandedDescription(carwash)
    : hasParcelFacts
      ? buildParcelExpandedDescription(parcel)
    : isCharging
      ? buildChargingExpandedDescription(chargingStation, chargingStationSource, destinationName)
    : content.expandedDescription;
  const primaryAction = isParkingPaymentCard
    ? `立即支付 ￥${parkingPaymentInfo.amountYuan}`
    : isParcel || isLiteParcel
        ? "导航去取件"
      : content.primaryAction;
  const secondaryAction = isParkingPaymentCard
    ? "取消"
    : isParcel
      ? "查看详情"
      : content.secondaryAction;

  function handlePrimaryAction() {
    if (isParkingPaymentCard) {
      onParkingPaymentPaid?.();
      return;
    }

    if (isRefuel) {
      onConfirm();
      return;
    }

    if (isParcel || isLiteParcel) {
      onConfirm();
      return;
    }

    onConfirm();
  }

  function handleSecondaryAction() {
    if (isParkingPaymentCard) {
      onCancelParkingPayment?.();
      return;
    }

    if (isParcel) {
      onOpenParcelDetail?.();
      onClose();
      return;
    }

    onClose();
  }

  if (isParkingPaymentCard) {
    return (
      <section
        className="night-ai-expanded-card night-ai-expanded-card--payment-phone"
        aria-label="支付宝停车缴费详情"
      >
        <span className="night-ai-expanded-card__surface" aria-hidden="true" />
        <div className="night-ai-expanded-card__phone-screen night-ai-expanded-card__phone-screen--image">
          <img
            className="night-ai-expanded-card__phone-image"
            src={PARKING_PAYMENT_IMAGE_SRC}
            alt={`支付宝停车缴费，待支付 ${parkingPaymentInfo.amountYuan} 元，${parkingPaymentInfo.lotName}，车牌 ${parkingPaymentInfo.plateNumber}，停留 ${parkingPaymentInfo.duration}`}
            draggable={false}
          />
          <button
            type="button"
            className="night-ai-expanded-card__phone-hotspot night-ai-expanded-card__phone-hotspot--close"
            aria-label="关闭支付页"
            onClick={() => onCancelParkingPayment?.()}
          />
          <button
            type="button"
            className="night-ai-expanded-card__phone-hotspot night-ai-expanded-card__phone-hotspot--pay"
            aria-label="立即支付"
            onClick={() => onParkingPaymentPaid?.()}
          />
        </div>
      </section>
    );
  }

  return (
    <section
      className={[
        "night-ai-expanded-card",
        isLargeCard ? "night-ai-expanded-card--large" : "",
      ].filter(Boolean).join(" ")}
      aria-label={`${expandedTitle}详情`}
    >
      {isLargeCard ? (
        <NightLargeGlowCardSurface />
      ) : (
        <span className="night-ai-expanded-card__surface" aria-hidden="true" />
      )}
      <span className="night-ai-expanded-card__content-mask" aria-hidden="true" />
      <div className={`night-ai-scenario-icon night-ai-scenario-icon--${content.icon} night-ai-expanded-card__icon`}>
        <NightAiScenarioIcon icon={content.icon} />
      </div>
      <div className="night-ai-expanded-card__main">
        <h2>{expandedTitle}</h2>
        <p>{expandedDescription}</p>
      </div>
      <div className="night-ai-expanded-card__actions">
        <button type="button" className="night-ai-expanded-card__action night-ai-expanded-card__action--primary" onClick={handlePrimaryAction}>
          {primaryAction}
        </button>
        <button type="button" className="night-ai-expanded-card__action night-ai-expanded-card__action--secondary" onClick={handleSecondaryAction}>
          {secondaryAction}
        </button>
      </div>
    </section>
  );
}

function buildChargingExpandedDescription(
  station: ChargingStation,
  source: ChargingStationSource,
  destinationName: string
) {
  if (source === "loading") {
    return `${destinationName}附近有充电站，电价和停车信息更新中。`;
  }

  const { chargePriceLabel, parkingFeeLabel } = buildChargingPriceFields(station);
  return `${destinationName}附近有充电站，距你约${formatRouteDistance(station.routeDistanceKm)}，电价${chargePriceLabel}，停车${parkingFeeLabel}。`;
}

function buildChargingPriceFields(station: ChargingStation) {
  return {
    chargePriceLabel: `${formatPrice(station.pricePerKwh)}元/度`,
    parkingFeeLabel: station.parkingFee
  };
}

function formatPrice(price: number) {
  return Number.isInteger(price) ? String(price) : price.toFixed(2).replace(/0$/, "");
}

function formatRouteDistance(distanceKm: number) {
  return distanceKm < 1 ? `${Math.round(distanceKm * 1000)}m` : `${distanceKm.toFixed(1)}km`;
}

function buildParcelExpandedDescription(parcel: CockpitParcelData | undefined) {
  if (!parcel || parcel.pendingCount <= 0) {
    return "暂无待取包裹，保持当前路线。";
  }

  if (!parcel.onRoute) {
    return `家附近${parcel.pickupPoint.name}有${parcel.pendingCount}件待取包裹，待快递同步驿站后再导航。`;
  }

  return `家附近${parcel.pickupPoint.name}有${parcel.pendingCount}件待取包裹，预计${parcel.eta}到达。`;
}

function buildCarwashExpandedDescription(carwash: CockpitCarwashData | undefined) {
  if (!carwash || carwash.source === "loading") {
    return "高德正在筛选顺路洗车门店，优惠和等待时间更新中。";
  }

  const shop = carwash.recommendedShop;
  return `高德已筛选${shop.shortName || shop.name}，${shop.priceLabel}，${shop.couponLabel}，预计等待${shop.waitMinutes}分钟。`;
}

function buildRefuelRecommendation(state: CabinState, fuel?: CockpitFuelData) {
  if (fuel && fuel.source !== "loading") {
    const station = fuel.recommendedStation;

    return {
      station: station.shortName || station.name,
      location: `前方${station.routeDistanceKm.toFixed(1)}km`,
      grade: station.gradeLabel,
      amount: station.amountLabel,
      detour: `绕路${station.detourKm.toFixed(1)}km，排队${station.queueMinutes}分钟`
    };
  }

  const targetRangeKm = Math.max(260, Math.min(520, state.destinationDistanceKm * 3 + 180));
  const targetFuelLevel = (targetRangeKm / MAX_FUEL_RANGE_KM) * 100;
  const missingFuelLevel = Math.max(0, targetFuelLevel - state.fuelLevel);
  const recommendedLiters = Math.max(12, Math.min(42, Math.ceil((missingFuelLevel / 100) * 60 / 2) * 2));
  const amount = Math.ceil((recommendedLiters * 7.88) / 10) * 10;
  const detourMinutes = state.traffic === "heavy" ? 8 : state.traffic === "medium" ? 5 : 3;

  return {
    station: "中石化滨江站",
    location: "前方1.6km",
    grade: "92#",
    amount: `${amount}元`,
    detour: `绕路${detourMinutes}分钟`
  };
}
